import type { OutboxEntry } from './outbox-store';

/**
 * Map a replayed mutation's URL to the TanStack Query keys to invalidate once the server has applied
 * it. We can't reuse each hook's `onSuccess` (the React component that fired the mutation is long
 * gone by replay time), so we re-derive the affected keys from the URL path. The keys MUST mirror the
 * `familyKeys`/`adminKeys` factories in `@kidshome/api-contract` (broad prefixes are fine — TanStack
 * matches by prefix, so invalidating `['economy']` refreshes every economy view safely).
 *
 * Coverage = every offline-queueable (idempotent) write in the family + admin layers:
 *   - occurrence complete/approve → tasks + economy (coins may have moved)
 *   - template create → admin templates list + tasks (the calendar projects templates, ADR-0142)
 *   - habit create / check-in/uncheck/excuse/backfill → habits + admin habits + economy
 *   - quest create / objective complete / pitch-in → quests
 *   - role create → the admin roles list
 *   - redemption request / grant / deny → economy (rewards + balances + ledger) + admin redemptions
 *   - savings deposit, balance adjustment, hold place → economy
 *   - achievement-bonus create → economy + the admin bonuses list
 *   - learning-goal create/update/delete → learning + the admin per-child goals lists
 *   - notification mark-read / mark-all-read → notifications (badge + center list)
 *   - platform-console writes (suspend/reinstate, system-events, features, bundles, roles) → platform
 *   - library template create/edit → library (platform list + household catalog)
 * Anything unrecognized falls back to a broad refresh so a queued write never leaves stale data.
 */
export function invalidationKeysFor(entry: OutboxEntry): Array<readonly unknown[]> {
  const { url } = entry;

  if (
    url.includes('/tasks/occurrences/') ||
    url.includes('/tasks/complete') ||
    url.includes('/tasks/skip') ||
    url.includes('/tasks/uncomplete') ||
    url.includes('/tasks/photo-proof-complete')
  ) {
    // The `(templateId, localDate)` chore actions (ADR-0142) + the id-addressed occurrence actions:
    // completion/approval/undo can credit or claw back coins AND advance a cooperative family-quest
    // objective (ADR-0032), so refresh tasks, the economy caches, AND the quests card — mirroring
    // use-complete-task's online onSettled (which invalidates familyKeys.questsActive()).
    return [['tasks'], ['economy'], ['quests']];
  }
  if (url.includes('/tasks/templates')) {
    // A replayed template create refreshes the admin Chores list (adminKeys.templates) AND the task
    // projection: a new template materializes projected cards on the family calendar (ADR-0142).
    return [['admin', 'templates'], ['tasks']];
  }
  if (url.includes('/economy/bonuses')) {
    // A replayed bonus create refreshes the admin Bonuses list (adminKeys.bonuses) on top of the
    // broad economy views (Ways-to-earn shows the rule) — mirrors the redemptions pattern below.
    return [['economy'], ['admin', 'economy', 'bonuses']];
  }
  if (url.includes('/economy/redemptions')) {
    // Request (child) or grant/deny (admin) — refresh the household queue + all balance/reward views.
    return [['economy'], ['admin', 'economy', 'redemptions']];
  }
  if (
    url.includes('/economy/savings/') ||
    url.includes('/economy/adjustments') ||
    url.includes('/economy/holds')
  ) {
    return [['economy'], ['admin', 'economy', 'analytics']];
  }
  if (url.includes('/habits')) {
    // Slash-less so the collection create (POST /v1/habits) matches too — only idempotent WRITES are
    // ever queued, so GET /v1/habits/today can never reach this mapper, and the return below already
    // covers the create (adminKeys.habits + familyKeys.habitsToday).
    // A replayed check-in/uncheck/excuse/backfill changes the habit today-list + streaks (the
    // `['habits']` prefix matches both familyKeys.habitsToday and habitsHistory), and a check-in can
    // credit coins AND advance an 'any habit/both' family-quest objective (ADR-0032), so refresh the
    // economy caches AND the quests card too — mirroring use-check-habit's online onSettled. Also
    // refresh the guardian habit-detail calendar/streak view (`['admin','habits',id]` =
    // adminKeys.habit) — excuse/unexcuse + backfill are offline-queueable and their online paths
    // invalidate that admin key, but the `['habits']` prefix does NOT match it.
    return [['habits'], ['admin', 'habits'], ['economy'], ['quests']];
  }
  if (url.includes('/economy/')) {
    return [['economy']];
  }
  if (url.includes('/play/')) {
    // A replayed hero/pet/background unlock or equip (POST /play/hero|hero/unlock|pet|pet/unlock|
    // background|background/unlock) changes the play read surfaces AND spends ✨, so mirror the online
    // path (invalidateHeroSurfaces, entities/hero/invalidate.ts) for the replay path. The `['play']`
    // prefix matches familyKeys.playState (['play','state',…]); the generated hero/heroes/pets/
    // backgrounds/voice keys are url-keyed (['/v1/play/hero',…]), matched by the `['/v1/play']` prefix;
    // `['cosmetics']` covers the hero-scoped catalog + equipped looks; `['economy']` covers the ✨ balance.
    return [['play'], ['/v1/play'], ['cosmetics'], ['economy']];
  }
  if (url.includes('/cosmetics/')) {
    // A purchase debits coins (the sink) and changes ownership/equipped state, so refresh the cosmetics
    // catalog + equipped views AND the economy caches (the coin balance moved).
    return [['cosmetics'], ['economy']];
  }
  if (url.includes('/learning/goals')) {
    // A replayed goal create/update/delete refreshes the admin per-child goals lists — childId is not
    // derivable from the update/delete URLs, so the 3-segment prefix covers every
    // adminKeys.learningGoals(childId) key — plus the kid learning surfaces (goal status/target).
    return [['learning'], ['admin', 'learning', 'goals']];
  }
  if (url.includes('/learning/')) {
    // A replayed learning attempt updates mastery + makes the next item stale (ADR-0027), AND credits
    // coins/effort-stars server-side, so refresh both learning views AND the economy caches (balance
    // moved). A COMPLETED round also grows the hero's bond/level (ADR-0095), so mirror the online
    // path (invalidateHeroSurfaces, entities/hero/invalidate.ts) by refreshing the play read surfaces
    // (['play'] = familyKeys.playState; ['/v1/play'] = the url-keyed hero/heroes/pets/backgrounds/voice
    // keys) AND the hero-scoped cosmetics catalog/equipped looks (['cosmetics']) — the replay path is the
    // only reconcile for an offline write, and `data.done` is falsy offline so the online onSuccess never
    // fires the hero invalidation.
    return [['learning'], ['economy'], ['play'], ['/v1/play'], ['cosmetics']];
  }
  if (url.includes('/platform-admin/notifications')) {
    // A replayed platform announcement refreshes the console's Announcements stats/recent slices —
    // the ['platform','notifications'] prefix matches platformKeys.notifications/notificationStats/
    // notificationRecent. MUST sit before the '/notifications/' branch: the announce URL
    // (/v1/platform-admin/notifications/announcements) would otherwise match it and invalidate only
    // the member bell keys.
    return [['platform', 'notifications']];
  }
  if (url.includes('/notifications/')) {
    // A replayed mark-read/mark-all-read reconciles the unread badge + the center list — the
    // ['notifications'] prefix matches both notificationKeys.unreadCount and notificationKeys.list,
    // mirroring the hooks' own onSettled.
    return [['notifications']];
  }
  if (url.includes('/quests')) {
    // Slash-less so the collection create (POST /v1/quests) matches on top of the objective actions.
    // A replayed quest create / objective complete / pitch-in (POST /v1/quests/:questId/objectives/
    // :objectiveId/complete|pitch-in) changes the shared cooperative-quest surfaces, so refresh them —
    // mirroring use-complete-quest-objective/use-pitch-in-quest's online onSuccess. The `['quests']`
    // prefix matches both familyKeys.questsActive (['quests','active']) and questsHistory
    // (['quests','history']).
    return [['quests']];
  }
  if (url.includes('/platform-admin/templates')) {
    // A replayed library-template create/edit refreshes the platform Library list AND the
    // household-side catalog — the ['library'] prefix matches both templateKeys.platform and
    // templateKeys.catalog. MUST sit before the '/platform-admin/' and '/roles' branches.
    return [['library']];
  }
  if (url.includes('/platform-admin/')) {
    // Every other replayed platform-console write (suspend/reinstate household, system-event
    // resolve/reopen, feature toggles, hero-bundle config, member-role assignment, …) — the
    // ['platform'] prefix matches every platformKeys factory. MUST sit before the '/roles'
    // branch: the assign-member-roles URL (…/members/:memberId/roles) would otherwise match
    // '/roles' and invalidate the wrong ['admin','roles'] key.
    return [['platform']];
  }
  if (url.includes('/roles')) {
    // A replayed custom-role create (POST /v1/roles) refreshes the admin Roles list (adminKeys.roles).
    // Only the create is offline-queueable — useUpdateRole carries no Idempotency-Key.
    return [['admin', 'roles']];
  }
  // Unknown write — be safe and refetch the data-bearing namespaces.
  return [['tasks'], ['economy'], ['members']];
}
